import React, {useEffect, useState} from 'react';
import {Button, LogoSmall} from 'components';
import {detachedSearchBarStyles} from 'components/Basic/Form/Select/styles';
import {FormSelect} from 'components/Basic/Form/V2';
import Modal from 'components/Basic/Modal';
import {ResetIcon} from 'components/Basic/SvgIcon/ResetIcon';
import {BlueLogoTextHeaderExternal} from 'components/Images/BlueLogoTextExternal';
import {conditionsOptionsByCountry} from 'features/Provider/Register/InternationalProviderOnboarding/constants';
import {useLocale, useSetPreferredLanguage, useWindowSize} from 'hooks';
import i18next from 'i18next';
import {DirectBookingProfession} from 'interfaces';
import {Trans, useTranslation} from 'react-i18next';
import {useHistory} from 'react-router';
import styled from 'styled-components';
import {getCountryCodeFromLocale, getDirectoryLogoUrl} from 'utils';

import {useDirectBookingFilter} from '../useDirectBookingFilter';

import {CaretRightIcon, FilterBarsIcon} from './Icons';
import SearchBox from './SearchBox';

const Span = styled.span`
  background: linear-gradient(to right, #c026d3, #a855f7);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;
`;

type Props = ReturnType<typeof useDirectBookingFilter>;

export const DirectBookingProvideListHeader = ({
  conditionOptions,
  control,
  setValue,
  isLoadingOptions,
  paymentTypeOptions,
  professionOptions,
  stateOptions,
  selectedOptions,
  setAllowInternalUpdate,
  setSelectedOptions,
  reset,
  getValues,
}: Props) => {
  const {width} = useWindowSize();
  const isMobileScreen = Boolean(width && width < 768);

  // const [isActive, setIsActive] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);

  const isPhysician =
    selectedOptions?.profession === DirectBookingProfession.physician;

  const {locale, localeOptions, setLocale} = useLocale();
  const isInternationalUser = locale !== 'en-us';
  const history = useHistory();
  useSetPreferredLanguage();

  const countryCode = getCountryCodeFromLocale(locale) ?? 'US';

  const {t} = useTranslation();

  const getCustomDetachedStyles = (
    detachedStyles: any,
    overrides: Partial<ReturnType<typeof detachedStyles.detachedButton>> = {},
  ) => ({
    ...detachedStyles,
    detachedButton: (isOpen: boolean, hasValue: boolean) => {
      const base = detachedStyles.detachedButton?.(isOpen, hasValue) || {};
      return {
        ...base,
        borderColor: isOpen ? '#2E62EC' : '#D9D9D9',
        color: hasValue ? '#2E62EC' : '#666666',
        ...overrides,
      };
    },
    dropdownMenu: () => ({...detachedStyles.dropdownMenu?.(isMobileScreen)}),
  });

  // For Mobile: Handling Buttons Click
  const handleFilterMenuOpen = () => setOpenMenu(true);
  const handleFilterMenuClose = () => setOpenMenu(false);

  const applyFilterOptions = () => {
    const values = getValues();
    setSelectedOptions({
      state: values.state,
      paymentType: values.paymentType,
      country: values.country,
      conditions: values.conditions.filter(c => !!c) as string[],
      profession: values.profession,
      languages: values.languages?.filter(c => !!c) as string[],
      ethnicity: values.ethnicity?.filter(c => !!c) as string[],
      education: values.education,
      searchText: values.searchText,
    });
    setTimeout(handleFilterMenuClose, 100);
  };

  const applyResetFilter = () => {
    reset({
      country: 'United States',
      state: '',
      paymentType: '',
      conditions: [],
      profession: '',
      languages: [],
      ethnicity: [],
      education: '',
      searchText: '',
    });
  };

  useEffect(() => {
    if (isMobileScreen) {
      setAllowInternalUpdate(false);
    } else {
      handleFilterMenuClose();
      setAllowInternalUpdate(true);
    }
  }, [isMobileScreen]);

  const renderOptions = () => (
    <div className="md:pt-8 pb-2 flex flex-col md:grid md:grid-cols-7 gap-5 items-center">
      <FormSelect
        control={control}
        label={null}
        id="country"
        placeholder={t('country', 'Country')}
        extractValue={true}
        isSearchable={true}
        options={localeOptions}
        classes="w-full inline-block bg-white rounded-xl text-sm text-left pt-0 box-content"
        detachedSearchBar={true}
        styles={getCustomDetachedStyles(detachedSearchBarStyles)}
        onChange={(newVal: any) => {
          setLocale(newVal.value);
          i18next.changeLanguage(newVal.value);
          history.replace(`/${newVal.value}/direct-booking/providers`);
        }}
      />
      {!isInternationalUser ? (
        <>
          <FormSelect
            control={control}
            id="profession"
            label={null}
            options={professionOptions}
            isLoading={isLoadingOptions}
            extractValue={true}
            isSearchable={true}
            placeholder={t('profession', 'Profession')}
            classes="w-full inline-block bg-white rounded-xl text-sm text-left pt-0 box-content"
            detachedSearchBar={true}
            styles={getCustomDetachedStyles(detachedSearchBarStyles)}
          />

          <FormSelect
            control={control}
            id="state"
            label={null}
            options={stateOptions}
            isLoading={isLoadingOptions}
            isSearchable={true}
            extractValue={true}
            placeholder={t('your_state', 'Your state')}
            classes="w-full inline-block bg-white rounded-xl text-sm text-left pt-0 box-content"
            detachedSearchBar={true}
            styles={getCustomDetachedStyles(detachedSearchBarStyles)}
          />
        </>
      ) : null}
      {!isPhysician ? (
        <FormSelect
          control={control}
          id="conditions"
          label={null}
          options={
            !isInternationalUser
              ? conditionOptions
              : conditionsOptionsByCountry[countryCode] ??
                conditionsOptionsByCountry.US
          }
          isLoading={!isInternationalUser ? isLoadingOptions : false}
          extractValue={true}
          isMulti={true}
          isSearchable={true}
          placeholder={t('condition', 'Conditions')}
          classes="w-full inline-block bg-white rounded-xl text-sm text-left pt-0 box-content"
          selectionCountPrefix="Conditions"
          detachedSearchBar={true}
          styles={getCustomDetachedStyles(detachedSearchBarStyles)}
        />
      ) : null}
      {!isInternationalUser ? (
        <FormSelect
          control={control}
          id="paymentType"
          label={null}
          options={paymentTypeOptions}
          isLoading={isLoadingOptions}
          isSearchable={true}
          extractValue={true}
          placeholder={t('your_insurance', 'Your insurance')}
          classes="w-full inline-block bg-white rounded-xl text-sm text-left pt-0 box-content"
          detachedSearchBar={true}
          styles={getCustomDetachedStyles(detachedSearchBarStyles)}
        />
      ) : null}

      {/* Providers Search Bar */}
      <div className="col-span-2 w-full  md:grid md:grid-cols-5 md:gap-5 justify-center item-center p-0">
        <SearchBox
          control={control}
          setValue={setValue}
          classes="col-span-5 w-full md:col-span-4"
        />
        {/* Clear Filters Button */}
        <div className="hidden md:flex col-span-1 items-center justify-end">
          <Button
            onClick={applyResetFilter}
            btnType="black"
            outline
            className="group w-full h-full p-2 flex justify-center items-center"
          >
            <ResetIcon className="w-5 h-5 text-black group-hover:text-white" />
          </Button>
        </div>
      </div>
    </div>
  );

  // External Wellnite website domain link
  const logoUrl = getDirectoryLogoUrl();

  return (
    <>
      <div className="w-full flex flex-col px-4 lg:px-14 py-5 md:py-7 bg-lightOrange shadow-sm sticky top-0 left-0 z-50">
        <div className="flex flex-row justify-start items-center gap-5 md:gap-8">
          {isMobileScreen ? (
            <a href={logoUrl} rel="noreferrer noopener" target="_blank">
              <LogoSmall />
            </a>
          ) : (
            <BlueLogoTextHeaderExternal linkUrl={logoUrl} />
          )}
          <h3 className="font-montserrat text-2xl font-semibold leading-tight tracking-tight md:mt-1">
            <Trans
              i18nKey="directory.headerTitle"
              components={{span: <Span />}}
            />
          </h3>
        </div>

        {isMobileScreen ? null : renderOptions()}

        <div className="flex flex-col md:flex-row items-center justify-center gap-3.5 mt-5 md:mt-0">
          <button
            className="w-full flex md:hidden flex-row justify-center items-center gap-2 font-inter text-base font-medium px-6 py-2.5 rounded-[30px] bg-white border uppercase hover:bg-blue-600 hover:text-white"
            onClick={handleFilterMenuOpen}
          >
            <FilterBarsIcon /> {t('directory.filterBtn', 'Filter')}
          </button>
          {/* <button
            className={classNames(
              'w-full md:w-auto flex flex-row justify-center items-center gap-2 font-inter text-base font-medium px-6 py-2.5 rounded-[30px] bg-white border',
              {
                '!bg-blue-600 text-white': isActive,
                'hover:bg-blue-600 hover:text-white': !isActive,
              },
            )}
            onClick={() => setIsActive(!isActive)}
          >
            <ArrowDownIcon />{' '}
            {t('directory.sortBtn', 'Sort by soonest available')}
          </button> */}
        </div>
      </div>

      {/* Mobile Menu */}
      <Modal
        messageType="none"
        isOpen={openMenu}
        buttonFn={handleFilterMenuClose}
        showCloseIcon={true}
        containerClasses="bg-white w-full min-w-[100vw] min-h-screen -m-4"
        classes="min-h-screen !overflow-y-auto flex flex-col justify-between webkit-full-h"
        closeIconClasses="text-black"
        titleClasses="h-auto p-4"
      >
        <div className="flex flex-col gap-4">
          {renderOptions()}
          <button
            className="w-full flex justify-center items-center gap-3 py-3.5 px-5 rounded-[30px] bg-black border text-white font-inter font-medium hover:bg-white hover:border-black hover:text-black"
            onClick={applyFilterOptions}
          >
            {t('directory.applyFilterBtn', 'Apply Filters')} <CaretRightIcon />
          </button>
          <button
            className="w-full flex justify-center items-center py-3.5 px-5 rounded-[30px] bg-white text-black border font-inter font-medium hover:bg-gray-900 hover:text-white"
            onClick={applyResetFilter}
          >
            {t('directory.resetBtn', 'Reset')}
          </button>
        </div>
      </Modal>
    </>
  );
};
